import { useCallback } from 'react';

import {
    type AccountAddress,
    Aptos,
    AptosConfig,
    Deserializer,
    generateSignedTransaction,
    Network,
    SignedTransaction,
} from '@aptos-labs/ts-sdk';
import { useWallet } from '@aptos-labs/wallet-adapter-react';
import { useMutation } from '@tanstack/react-query';

import { type FeasibilityResponse, type MutationExecuteStepArgs } from '@pkg/graphql/codegen/graphql';
import { usePetraWallet } from '@pkg/hooks/wallets/usePetraWallet';

import { callExecuteOrderStep } from '@app/client/resources/marketplace';

function bigIntReplacer(v) {
    return typeof v === 'bigint' ? v.toString() : v;
}
export function usePetraTransaction() {
    const aptosConfig = new AptosConfig({ network: Network.MAINNET });
    const aptos = new Aptos(aptosConfig);
    const { handlePetraConnect } = usePetraWallet();
    const { connected, account, signTransaction } = useWallet();
    const { mutateAsync: stepMutation } = useMutation({
        mutationFn: ({ variables }: { variables: MutationExecuteStepArgs }) => callExecuteOrderStep({ variables }),
    });

    return useCallback(
        async (prepareRes: FeasibilityResponse) => {
            if (!connected) {
                await handlePetraConnect();
            }
            const transactionPayload = prepareRes.origin;
            const transaction = await aptos.transaction.build.simple({
                sender: account?.address as unknown as AccountAddress,
                data: {
                    function: transactionPayload.function,
                    typeArguments: transactionPayload.type_arguments,
                    functionArguments: transactionPayload.arguments,
                },
            });
            if (transaction) {
                try {
                    const authRes = await signTransaction({ rawTransaction: transaction.rawTransaction }, true);

                    const signed = generateSignedTransaction({
                        transaction: transaction,
                        senderAuthenticator: authRes,
                    });

                    if (authRes) {
                        const deserializer = new Deserializer(signed);
                        const deserializedSignedTransaction = SignedTransaction.deserialize(deserializer);
                        const signedString = JSON.stringify(deserializedSignedTransaction, (_, v) => bigIntReplacer(v));

                        if (deserializedSignedTransaction) {
                            await stepMutation({
                                variables: {
                                    orderId: prepareRes.orderId,
                                    step: {
                                        aptosSubmitTransaction: { signedTransaction: signedString },
                                    },
                                },
                            });
                        }
                    }
                } catch (_e) {
                    return false;
                }
            }
        },
        [account?.address, aptos.transaction.build, connected, handlePetraConnect, signTransaction, stepMutation],
    );
}
